import type { DbClient } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { createIsomorphicFn } from "@tanstack/react-start";
import type { SharePreviewMedia } from "@wags/catalog/share-preview/share-preview.contract.ts";
import { getVercelOptimizedImageProps } from "@wags/ui/vercel-image.tsx";
import { normalizeRemoteMediaUrl } from "@wags/utils/media.ts";
import {
    getProviderHeadData,
    getProviderListingReferencesInitialQueryOptions,
    getProviderSharePreview,
    getProvidersBatchData,
    getProvidersCollection,
    prefetchProviders,
    providerReviewsPagesQueryOptions,
} from "@/features/providers";
import { seoMeta } from "@/lib/ui/seo";
import { sharePreviewSeoMedia } from "./dog-share-media";

export type ProviderHeadData = {
    name: string | null;
    city: string | null;
    state: string | null;
} | null;

function toProviderHeadData(
    org:
        | {
              name: string | null;
              kennel_name?: string | null;
              city: string | null;
              state: string | null;
          }
        | null
        | undefined,
): ProviderHeadData {
    if (!org) return null;
    return {
        name: org.kennel_name?.trim() || org.name,
        city: org.city,
        state: org.state,
    };
}

const loadProviderHead = createIsomorphicFn()
    .server(
        async ({
            providerId,
        }: {
            dbClient: DbClient;
            providerId: string;
        }): Promise<ProviderHeadData> => {
            try {
                return toProviderHeadData(
                    await getProviderHeadData(providerId),
                );
            } catch {
                return null;
            }
        },
    )
    .client(({ dbClient, providerId }): ProviderHeadData => {
        void prefetchProviders(dbClient, [providerId]).catch(() => {});
        return toProviderHeadData(
            getProvidersCollection(dbClient).get(providerId),
        );
    });

const loadProviderSharePreview = createIsomorphicFn()
    .server(async (providerId: string) => getProviderSharePreview(providerId))
    .client(
        async (_providerId: string): Promise<SharePreviewMedia | null> => null,
    );

export type ProviderRouteLoaderData = {
    head: ProviderHeadData;
    firstImageUrl: string | null;
    sharePreview: SharePreviewMedia | null;
};

const PROVIDER_CARD_IMAGE_WIDTH = 220;
const PROVIDER_CARD_IMAGE_HEIGHT = 275;

function providerCardPreload(url: string | null) {
    if (!url) return [];
    const normalized = normalizeRemoteMediaUrl(url) ?? url;
    const image = getVercelOptimizedImageProps(
        normalized,
        PROVIDER_CARD_IMAGE_WIDTH,
        PROVIDER_CARD_IMAGE_HEIGHT,
    );
    return [
        {
            rel: "preload" as const,
            as: "image" as const,
            href: image.src,
            ...("srcSet" in image
                ? { imageSrcSet: image.srcSet, imageSizes: image.sizes }
                : {}),
            fetchPriority: "high" as const,
        },
    ];
}

function providerSeoMeta(
    head: ProviderHeadData | undefined,
    providerId: string,
    sharePreview: SharePreviewMedia | null | undefined,
    subpage?: { title: string; noindex: boolean },
) {
    const shareMedia = sharePreview ? sharePreviewSeoMedia(sharePreview) : null;
    const name = head?.name?.trim();
    const location = [head?.city, head?.state]
        .map((v) => v?.trim())
        .filter(Boolean)
        .join(", ");
    const title = name ? `${name} | Wags` : "Provider Profile — Wags";
    return seoMeta({
        title: subpage ? `${subpage.title} - ${title}` : title,
        description: name
            ? `${name}${location ? ` in ${location}` : ""} on Wags. See their available dogs, photos, and reviews.`
            : "View this provider on Wags — breeders, shelters, and rescues matched to your lifestyle.",
        url: `/provider/${providerId}`,
        image: shareMedia?.url,
        imageMimeType: shareMedia?.mimeType,
        video: shareMedia?.video,
        noindex: subpage?.noindex,
    });
}

type ProviderSubpageArgs = {
    context: { dbClient: DbClient; queryClient: QueryClient };
    params: { providerId: string };
};

function providerSubpageHead(subpage: { title: string; noindex: boolean }) {
    return ({
        loaderData,
        params,
    }: Pick<ProviderSubpageArgs, "params"> & {
        loaderData?: {
            head: ProviderHeadData;
            sharePreview?: SharePreviewMedia | null;
        };
    }) =>
        providerSeoMeta(
            loaderData?.head,
            params.providerId,
            loaderData?.sharePreview,
            subpage,
        );
}

const loadProviderRecord = createIsomorphicFn()
    .server(
        async ({ providerId }: { dbClient: DbClient; providerId: string }) => {
            try {
                const [record] = await getProvidersBatchData({
                    organizationIds: [providerId],
                });
                return record;
            } catch {
                return undefined;
            }
        },
    )
    .client(async ({ dbClient, providerId }) => {
        await prefetchProviders(dbClient, [providerId]).catch(() => {});
        return getProvidersCollection(dbClient).get(providerId);
    });

const prefetchProviderReviews = createIsomorphicFn()
    .server(async (queryClient: QueryClient, providerId: string) => {
        await queryClient.prefetchInfiniteQuery(
            providerReviewsPagesQueryOptions(providerId),
        );
    })
    .client((queryClient: QueryClient, providerId: string) => {
        void queryClient.prefetchInfiniteQuery(
            providerReviewsPagesQueryOptions(providerId),
        );
    });

export const catalogProviderAboutRoute = {
    ssr: true as const,
    loader: async ({ context, params }: ProviderSubpageArgs) => {
        const [record, sharePreview] = await Promise.all([
            loadProviderRecord({
                dbClient: context.dbClient,
                providerId: params.providerId,
            }),
            loadProviderSharePreview(params.providerId),
        ]);
        const about = record?.description?.trim() || null;
        const mission = record?.missionStatement?.trim() || null;
        if (!about && !mission) {
            throw redirect({
                to: "/provider/$providerId",
                params: { providerId: params.providerId },
                replace: true,
            });
        }
        return {
            head: toProviderHeadData(record),
            about,
            mission,
            sharePreview,
        };
    },
    head: providerSubpageHead({ title: "About", noindex: false }),
};

export const catalogProviderReviewsRoute = {
    ssr: true as const,
    loader: async ({ context, params }: ProviderSubpageArgs) => {
        const record = loadProviderRecord({
            dbClient: context.dbClient,
            providerId: params.providerId,
        });
        await prefetchProviderReviews(context.queryClient, params.providerId);
        return { head: toProviderHeadData(await record) };
    },
    head: providerSubpageHead({ title: "Reviews", noindex: true }),
};

export const catalogProviderRoute = {
    ssr: true as const,

    loader: async ({
        context,
        params,
    }: {
        context: { dbClient: DbClient; queryClient: QueryClient };
        params: { providerId: string };
    }): Promise<ProviderRouteLoaderData> => {
        const [headResult, pageResult, sharePreviewResult] =
            await Promise.allSettled([
                loadProviderHead({
                    dbClient: context.dbClient,
                    providerId: params.providerId,
                }),
                context.queryClient.ensureQueryData(
                    getProviderListingReferencesInitialQueryOptions(
                        params.providerId,
                    ),
                ),
                loadProviderSharePreview(params.providerId),
            ]);
        const page =
            pageResult.status === "fulfilled" ? pageResult.value : null;
        return {
            head: headResult.status === "fulfilled" ? headResult.value : null,
            firstImageUrl: page?.listingReferences[0]?.primaryImageUrl ?? null,
            sharePreview:
                sharePreviewResult.status === "fulfilled"
                    ? sharePreviewResult.value
                    : null,
        };
    },
    head: ({
        loaderData,
        params,
    }: {
        loaderData?: ProviderRouteLoaderData;
        params: { providerId: string };
    }) => {
        const meta = providerSeoMeta(
            loaderData?.head,
            params.providerId,
            loaderData?.sharePreview,
        );
        return {
            ...meta,
            links: [
                ...meta.links,
                ...providerCardPreload(loaderData?.firstImageUrl ?? null),
            ],
        };
    },
};
